import React from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Stack,
  Avatar,
  CircularProgress,
  Paper,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Fade,
  Chip,
  FormHelperText,
} from "@mui/material";
import ConstructionIcon from "@mui/icons-material/Construction";
import KeyIcon from "@mui/icons-material/Key";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface AgentIdentity {
  name: string;
  provider: string;
  role: string;
  description: string;
}

interface KnowledgeBase {
  docIndexes: any[];
  selectedDocIndex: string | null;
}

interface ToolsConfig {
  discoveredTools: any[];
  toolKeySelections: { [toolName: string]: string };
  availableKeys: any[];
}

interface ExpressAgentReviewOrEditProps {
  identity: AgentIdentity;
  onIdentityChange: (field: keyof AgentIdentity, value: string) => void;
  knowledgeBase: KnowledgeBase;
  onKnowledgeBaseChange: (indexId: string | null) => void;
  tools: ToolsConfig;
  onToolKeySelect: (toolName: string, keyId: string) => void;
  onCancel: () => void;
  onCreate: () => void;
  isCreating: boolean;
  toolsRequireKeys: boolean;
  allKeysSelected: boolean;
  isEditMode?: boolean;
}

const ExpressAgentReviewOrEdit: React.FC<ExpressAgentReviewOrEditProps> = ({
  identity,
  onIdentityChange,
  knowledgeBase,
  onKnowledgeBaseChange,
  tools,
  onToolKeySelect,
  onCancel,
  onCreate,
  isCreating,
  toolsRequireKeys,
  allKeysSelected,
  isEditMode = false,
}) => {
  return (
    <Fade in={true}>
      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="flex-start"
        >
          {/* Identity & Role Section */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Card
                sx={{
                  p: 3,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "primary.main", mb: 2 }}
                >
                  Identity & Role
                </Typography>
                <Stack spacing={2} sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Agent Name"
                    variant="filled"
                    value={identity.name}
                    onChange={(e) => onIdentityChange("name", e.target.value)}
                  />
                  <Autocomplete
                    freeSolo
                    forcePopupIcon
                    options={["OpenAI", "Anthropic", "Gemini"]}
                    value={identity.provider}
                    onChange={(_, val) =>
                      onIdentityChange("provider", val || "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Provider (optional)"
                        variant="filled"
                      />
                    )}
                  />
                  <TextField
                    fullWidth
                    label="Role"
                    variant="filled"
                    value={identity.role}
                    onChange={(e) => onIdentityChange("role", e.target.value)}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Description"
                    variant="filled"
                    value={identity.description}
                    onChange={(e) =>
                      onIdentityChange("description", e.target.value)
                    }
                  />
                </Stack>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "primary.main", mb: 2 }}
                >
                  Knowledge Space
                </Typography>
                <Autocomplete
                  options={[
                    { index_id: null, name: "None" },
                    ...knowledgeBase.docIndexes,
                  ]}
                  getOptionLabel={(opt: any) => opt.name || opt.index_id || "None"}
                  value={
                    knowledgeBase.selectedDocIndex === null
                      ? { index_id: null, name: "None" }
                      : knowledgeBase.docIndexes.find(
                          (i: any) => i.index_id === knowledgeBase.selectedDocIndex,
                        ) || null
                  }
                  onChange={(_, value) =>
                    onKnowledgeBaseChange(value?.index_id || null)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Document Collection"
                      variant="filled"
                      helperText="Select a knowledge base or None to remove"
                    />
                  )}
                />
              </Card>
            </Paper>
          </Box>

          {/* Discovered Tools Section - Conditionally Rendered */}
          {tools.discoveredTools.length > 0 && (
            <Box sx={{ flex: 1, width: "100%" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                }}
              >
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: "secondary.main",
                        width: 40,
                        height: 40,
                      }}
                    >
                      <ConstructionIcon />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{ color: "primary.main" }}
                      >
                        Tools ({tools.discoveredTools.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Automatically selected based on intent.
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack spacing={2}>
                    {tools.discoveredTools.map((tool) => {
                      const requiresKey =
                        Array.isArray(tool.keys_schema) &&
                        tool.keys_schema.length > 0;
                      return (
                        <Box
                          key={tool.name}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: requiresKey
                              ? "warning.main"
                              : "divider",
                            bgcolor: "background.paper",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                          >
                            <Box sx={{ mt: 0.5 }}>
                              {requiresKey ? (
                                <KeyIcon
                                  color={!tools.toolKeySelections[tool.name] ? "error" : "warning"}
                                  sx={{ fontSize: 20 }}
                                />
                              ) : (
                                <CheckCircleIcon
                                  color="success"
                                  sx={{ fontSize: 20 }}
                                />
                              )}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 1 }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={700}
                                >
                                  {tool.display_name || tool.name}
                                </Typography>
                                {requiresKey &&
                                  !tools.toolKeySelections[tool.name] && (
                                    <Chip
                                      label="REQUIRED"
                                      color="error"
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: "0.65rem",
                                        fontWeight: 700,
                                      }}
                                    />
                                  )}
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mb: requiresKey ? 1.5 : 0,
                                }}
                              >
                                {tool.description}
                              </Typography>
                              {requiresKey && (
                                <FormControl
                                  fullWidth
                                  variant="filled"
                                  size="small"
                                  error={
                                    requiresKey &&
                                    !tools.toolKeySelections[tool.name]
                                  }
                                >
                                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                                    {tool.keys_schema[0]?.name || "api_key"}
                                  </InputLabel>
                                  <Select
                                    value={
                                      tools.toolKeySelections[tool.name] || ""
                                    }
                                    onChange={(e) =>
                                      onToolKeySelect(tool.name, e.target.value)
                                    }
                                  >
                                    {tools.availableKeys.map((k) => (
                                      <MenuItem key={k.id} value={k.id}>
                                        {k.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  {requiresKey &&
                                    !tools.toolKeySelections[tool.name] && (
                                      <FormHelperText sx={{ 
                                        fontWeight: 500, 
                                        fontSize: "0.90rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mx: 0}}>
                                          <ErrorOutlineIcon sx={{ fontSize: '1rem' }} />
                                        Key required to complete configuration,
                                      </FormHelperText>
                                    )}
                                </FormControl>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Card>
              </Paper>
            </Box>
          )}
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mt: 4, mb: 4 }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              size="large"
              variant="outlined"
              color={isEditMode ? "inherit" : "error"}
              onClick={onCancel}
              sx={{ borderRadius: 99, px: 4 }}
            >
              {isEditMode ? "Cancel" : "Discard Draft"}
            </Button>
          </Stack>
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onCreate}
            disabled={isCreating || (toolsRequireKeys && !allKeysSelected)}
            startIcon={
              isCreating ? <CircularProgress size={20} /> : <RocketLaunchIcon />
            }
            sx={{
              borderRadius: 99,
              px: 5,
              fontWeight: 700,
              boxShadow: "0 10px 30px rgba(184, 115, 51, 0.3)",
            }}
          >
            {isCreating
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"}
          </Button>
        </Stack>
      </Box>
    </Fade>
  );
};

export default ExpressAgentReviewOrEdit;
